<template>
  <div class="welfare">
    <div class="welfare__intro">
      <i class="bi bi-info-circle"></i>
      <div>
        Các mức xu dưới đây được backend dùng <b>trực tiếp</b> để trả xu cho tác giả và hiển thị ở mục
        <b>Phúc lợi</b> trên website. Thay đổi có hiệu lực từ lần trả xu tiếp theo (cron trả xu theo lượt đọc chạy 03:30 hằng ngày).
      </div>
    </div>

    <div v-if="loading" class="welfare__loading"><span class="spinner-border text-primary"></span></div>

    <template v-else>
      <!-- 1. Xu theo hạng -->
      <section class="welfare__section">
        <h3 class="welfare__title">Xu cơ bản mỗi lượt đọc theo hạng danh tiếng</h3>
        <div class="welfare__grid welfare__grid--4">
          <label v-for="r in ranks" :key="r.id" class="welfare__field" :class="{'is-dirty': isDirty(r.id)}">
            <span class="welfare__label">
              <span class="welfare__rank" :class="'welfare__rank--' + r.rank">{{ r.rank }}</span>{{ r.label }}
            </span>
            <div class="welfare__input">
              <NumberInput v-model="form[r.id]" placeholder="0"/>
              <span class="welfare__unit">xu / lượt đọc</span>
            </div>
          </label>
        </div>
      </section>

      <!-- 2. Độc quyền & app -->
      <section class="welfare__section">
        <h3 class="welfare__title">Xu theo loại lượt đọc</h3>
        <div class="welfare__grid welfare__grid--2">
          <label v-for="f in extraFields" :key="f.id" class="welfare__field" :class="{'is-dirty': isDirty(f.id)}">
            <span class="welfare__label">{{ f.label }}</span>
            <div class="welfare__input">
              <NumberInput v-model="form[f.id]" placeholder="0"/>
              <span class="welfare__unit">{{ f.unit }}</span>
            </div>
            <small class="welfare__hint">{{ f.hint }}</small>
          </label>
        </div>
      </section>

      <!-- 4. Phí -->
      <section class="welfare__section">
        <h3 class="welfare__title">Phí hệ thống</h3>
        <div class="welfare__grid welfare__grid--2">
          <label v-for="f in feeFields" :key="f.id" class="welfare__field" :class="{'is-dirty': isDirty(f.id)}">
            <span class="welfare__label">{{ f.label }}</span>
            <div class="welfare__input">
              <NumberInput v-model="form[f.id]" :max="100" placeholder="0"/>
              <span class="welfare__unit">%</span>
            </div>
          </label>
        </div>
      </section>

      <!-- Thanh lưu -->
      <div class="welfare__bar">
        <span v-if="dirtyIds.length" class="welfare__changes">Có {{ dirtyIds.length }} thay đổi chưa lưu</span>
        <span v-else class="welfare__changes text-muted">Không có thay đổi</span>
        <button class="btn btn-light" :disabled="!dirtyIds.length || saving" @click="reset()">Hoàn tác</button>
        <button class="btn btn-primary" :disabled="!dirtyIds.length || saving" @click="save()">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          Lưu thay đổi
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import requestUtil from "@/utils/request-util";
import NumberInput from "@/components/commons/NumberInput.vue";

// Mặc định khi chưa cấu hình – phải khớp AdminService.DEFAULT_* bên backend
const DEFAULTS = {
  AUTHOR_PRICE_VIEW_EXCLUSIVE: 30,
  AUTHOR_PRICE_VIEW_APP: 0,
};

export default {
  name: 'WelfareSettings',
  components: {NumberInput},
  data() {
    return {
      loading: true,
      saving: false,
      raw: {},      // ConfigSystem gốc theo id (giữ valueStr/values khi lưu)
      original: {}, // giá trị đang lưu
      form: {},     // giá trị đang sửa
      ranks: [
        {rank: 1, id: 'AUTHOR_PRICE_VIEW_RANK_1', label: 'Thành viên mới'},
        {rank: 2, id: 'AUTHOR_PRICE_VIEW_RANK_2', label: 'Tiềm năng'},
        {rank: 3, id: 'AUTHOR_PRICE_VIEW_RANK_3', label: 'Danh tiếng'},
        {rank: 4, id: 'AUTHOR_PRICE_VIEW_RANK_4', label: 'Chuyên nghiệp'},
      ],
      extraFields: [
        {id: 'AUTHOR_PRICE_VIEW_EXCLUSIVE', label: 'Truyện độc quyền Siêu Mọt', unit: 'xu / lượt đọc',
          hint: 'Thay cho xu cơ bản với lượt đọc truyện độc quyền'},
        {id: 'AUTHOR_PRICE_VIEW_APP', label: 'Thưởng thêm lượt đọc trên app', unit: 'xu / lượt đọc',
          hint: 'Cộng thêm cho mỗi lượt đọc từ iOS/Android (0 = không thưởng)'},
      ],
      milestones: [
        {label: '1K', composer: 'COMPOSER_1K_VIEW', translate: 'TRANSLATE_1K_VIEW'},
        {label: '5K', composer: 'COMPOSER_5K_VIEW', translate: 'TRANSLATE_5K_VIEW'},
        {label: '10K', composer: 'COMPOSER_10K_VIEW', translate: 'TRANSLATE_10K_VIEW'},
        {label: '100K', composer: 'COMPOSER_100K_VIEW', translate: 'TRANSLATE_100K_VIEW'},
      ],
      feeFields: [
        {id: 'FEE_ORDER_PERCENT', label: 'Phí khi độc giả mua chương/truyện khoá xu'},
        {id: 'FEE_DONATE_PERCENT', label: 'Phí khi độc giả donate cho tác giả'},
      ],
    };
  },
  computed: {
    allIds() {
      return [
        ...this.ranks.map((r) => r.id),
        ...this.extraFields.map((f) => f.id),
        ...this.feeFields.map((f) => f.id),
      ];
    },
    dirtyIds() {
      return this.allIds.filter((id) => this.isDirty(id));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    isDirty(id) {
      return Number(this.form[id]) !== Number(this.original[id]);
    },
    async load() {
      this.loading = true;
      try {
        const res = await requestUtil.get('/admin/configSystem');
        const byId = {};
        (res.data || []).forEach((c) => (byId[c.id] = c));
        this.raw = byId;
        const values = {};
        this.allIds.forEach((id) => {
          const v = byId[id]?.value;
          values[id] = v !== null && v !== undefined ? v : (DEFAULTS[id] ?? 0);
        });
        this.original = {...values};
        this.form = {...values};
      } catch (e) {
        this.$notify({title: 'Không tải được cấu hình', text: e?.message || '', type: 'warn'});
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.form = {...this.original};
    },
    async save() {
      const invalid = this.dirtyIds.filter((id) => !Number.isInteger(this.form[id]) || this.form[id] < 0);
      if (invalid.length) {
        this.$notify({title: 'Giá trị không hợp lệ', text: 'Chỉ nhập số nguyên ≥ 0', type: 'warn'});
        return;
      }
      if (!window.confirm(`Lưu ${this.dirtyIds.length} thay đổi? Mức xu mới sẽ áp dụng từ lần trả xu tiếp theo.`)) return;
      this.saving = true;
      let ok = 0;
      for (const id of this.dirtyIds) {
        try {
          const old = this.raw[id] || {};
          await requestUtil.post('/admin/configMoney/_single', {
            id,
            value: this.form[id],
            valueStr: old.valueStr ?? null,
            values: old.values ?? null,
          });
          this.original[id] = this.form[id];
          ok++;
        } catch (e) {
          this.$notify({title: `Lưu ${id} thất bại`, text: e?.message || '', type: 'warn'});
        }
      }
      this.saving = false;
      if (ok) this.$notify({title: `Đã lưu ${ok} thay đổi`, type: 'success'});
    },
  },
};
</script>

<style scoped>
.welfare { display: flex; flex-direction: column; gap: 20px; }
.welfare__intro {
  display: flex; gap: 10px; padding: 12px 16px; border-radius: 10px;
  background: #e9f3ff; color: #1b5fae; font-size: 13px; line-height: 1.6;
}
.welfare__intro i { font-size: 16px; margin-top: 1px; }
.welfare__loading { padding: 40px; text-align: center; }
.welfare__section { padding: 18px 20px; border: 1px solid #eff2f5; border-radius: 12px; background: #fff; }
.welfare__title { margin: 0 0 14px; font-size: 15px; font-weight: 700; color: #181c32; }
.welfare__grid { display: grid; gap: 12px; }
.welfare__grid--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.welfare__grid--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.welfare__field {
  display: flex; flex-direction: column; gap: 8px; padding: 12px 14px;
  border: 1px solid #eff2f5; border-radius: 10px; background: #fafbfc; margin: 0;
  transition: border-color .15s ease, background-color .15s ease;
}
.welfare__label { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #3f4254; }
.welfare__rank {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%; color: #fff; font-size: 12px; font-weight: 700;
}
/* màu theo khung huy hiệu từng hạng (giống website) */
.welfare__rank--1 { background: #e05a9e; }
.welfare__rank--2 { background: #c99a1e; }
.welfare__rank--3 { background: #d1262f; }
.welfare__rank--4 { background: #e8730c; }
.welfare__input { display: flex; align-items: center; gap: 8px; }
.welfare input {
  width: 100%; min-width: 80px; height: 38px; padding: 0 10px;
  border: 1px solid #e4e6ef; border-radius: 8px; background: #fff;
  font-size: 15px; font-weight: 600; color: #181c32; outline: none;
}
.welfare input:focus { border-color: #1b84ff; }
.welfare__unit { flex: 0 0 auto; font-size: 12px; color: #7e8299; white-space: nowrap; }
.welfare__hint { font-size: 12px; color: #a1a5b7; }
.welfare__table input { max-width: 180px; }
.welfare__table td { vertical-align: middle; }
/* Ô đã sửa chưa lưu */
.is-dirty { border-color: #e9a800 !important; background: #fff9e6 !important; }
td.is-dirty input { border-color: #e9a800; }
.welfare__bar {
  position: sticky; bottom: 0; z-index: 2;
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  padding: 12px 16px; border-radius: 12px; background: #fff;
  box-shadow: 0 -4px 16px rgba(24, 28, 50, .08);
}
.welfare__changes { margin-right: auto; font-size: 13px; font-weight: 600; color: #b07d00; }
@media (max-width: 991.98px) {
  .welfare__grid--4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 575.98px) {
  .welfare__grid--4, .welfare__grid--2 { grid-template-columns: 1fr; }
}
</style>
